/**
 * `/policies` — the Selection Policy library.
 *
 * From the mockup's `isPolLib` block, and it sits beside `/templates` rather than buried inside a
 * Liturgy, because a Policy is a reusable resource in the same sense a Template is
 * (docs/engineering/11-screens.md).
 *
 * The two explanatory columns are not decoration. The single most common misunderstanding this
 * product has to prevent is "delete the options we don't use from the Template", and the columns say
 * plainly what a Policy can and cannot do.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

// Only ever narrowed to, never constructed here — the screen reads a failure, it does not raise one.
import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { fetchPolicyLibrary, type PolicyLibraryRow } from './api';
import './policies.css';

export function PolicyLibraryScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const library = useQuery({ queryKey: ['policies'], queryFn: fetchPolicyLibrary });

  const scopes = [
    {
      scope: t('policies.library.scope_org_title'),
      who: t('policies.library.scope_org_who'),
      what: t('policies.library.scope_org_what'),
    },
    {
      scope: t('policies.library.scope_cleric_title'),
      who: t('policies.library.scope_cleric_who'),
      what: t('policies.library.scope_cleric_what'),
    },
    {
      scope: t('policies.library.scope_liturgy_title'),
      who: t('policies.library.scope_liturgy_who'),
      what: t('policies.library.scope_liturgy_what'),
    },
  ];

  return (
    <div className="pol-lib">
      <header className="pol-lib__head">
        <div>
          <p className="pol__eyebrow">{t('policies.library.eyebrow')}</p>
          <h1 className="pol-lib__title">{t('policies.library.title')}</h1>
        </div>
      </header>

      <p className="pol-lib__lede">{t('policies.library.lede')}</p>

      <section className="pol-lib__table">
        <div className="pol-lib__row pol-lib__row--head">
          <div>{t('policies.library.col_policy')}</div>
          <div>{t('policies.library.col_scope')}</div>
          <div>{t('policies.library.col_restricts')}</div>
          <div />
        </div>

        {library.isPending ? (
          <p className="pol-state">{t('policies.library.loading')}</p>
        ) : library.isError ? (
          <Notice referenceId={(library.error as ApiError).correlationId}>
            {(library.error as ApiError).message}
          </Notice>
        ) : library.data.data.length === 0 ? (
          <p className="pol-lib__empty">{t('policies.library.empty')}</p>
        ) : (
          library.data.data.map((row) => <LibraryRow key={row.id} row={row} />)
        )}
      </section>

      <div className="pol-lib__columns">
        <div>
          <p className="pol__eyebrow">{t('policies.library.how_defined_eyebrow')}</p>
          <div className="pol__card pol__card--flush">
            {scopes.map((entry) => (
              <div key={entry.scope} className="pol-lib__scope">
                <span className="pol__origin pol__origin--template">{entry.scope}</span>
                <div>
                  <div className="pol-lib__scope-who">{entry.who}</div>
                  <div className="pol-lib__scope-what">{entry.what}</div>
                </div>
              </div>
            ))}
            <p className="pol__panel-note">{t('policies.library.layers_note')}</p>
          </div>
        </div>

        <div>
          <p className="pol__eyebrow">{t('policies.library.can_say_eyebrow')}</p>
          <div className="pol__card pol-lib__says">
            <div>
              <h3>{t('policies.library.which_options_title')}</h3>
              <p>{t('policies.library.which_options_desc')}</p>
            </div>
            <div>
              <h3>{t('policies.library.who_may_fill_title')}</h3>
              <p>{t('policies.library.who_may_fill_desc')}</p>
            </div>
            <div>
              <h3>{t('policies.library.which_roles_title')}</h3>
              <p>{t('policies.library.which_roles_desc')}</p>
            </div>
            <div className="pol-lib__cannot">
              <p className="pol__eyebrow pol__eyebrow--red">
                {t('policies.library.cannot_do_eyebrow')}
              </p>
              <p>{t('policies.library.cannot_do_desc')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LibraryRow({ row }: { row: PolicyLibraryRow }): React.JSX.Element {
  const { t } = useTranslation('common');
  const restricts = [
    row.withheld_options > 0
      ? `${row.withheld_options} option${row.withheld_options === 1 ? '' : 's'} withheld`
      : null,
    row.restricted_modes > 0 ? `${row.restricted_modes} item access changed` : null,
    row.restricted_roles > 0 ? `${row.restricted_roles} role rules` : null,
  ].filter((part): part is string => part !== null);

  return (
    <div className="pol-lib__row">
      <div>
        <div className="pol-lib__name">{row.name}</div>
        <div className="pol-lib__meta">
          {row.owner_label} · revision {row.revision}
        </div>
      </div>

      <div>
        <span className={`pol__origin pol__origin--${row.scope}`}>{row.scope}</span>
      </div>

      <div>
        <div className="pol-lib__restricts">
          {restricts.length > 0 ? restricts.join(' · ') : t('policies.library.nothing_withheld')}
        </div>
        <div className="pol-lib__used">
          {row.restricted_items === 0
            ? t('policies.library.no_items_narrowed')
            : `${row.restricted_items} item${row.restricted_items === 1 ? '' : 's'} narrowed`}
        </div>
      </div>

      <div className="pol-lib__act">
        {row.scope === 'liturgy' ? (
          <Link
            className="pol__btn pol__btn--small"
            to="/liturgies/$id/selection-policy"
            params={{ id: row.owner_liturgy_id! }}
          >
            {t('policies.library.open')}
          </Link>
        ) : row.scope === 'organization' ? (
          <Link
            className="pol__btn pol__btn--small"
            to="/organizations/$id/selection-policy"
            params={{ id: row.raw_owner_id }}
          >
            {t('policies.library.open')}
          </Link>
        ) : (
          <Link className="pol__btn pol__btn--small" to="/me/selection-policy">
            {t('policies.library.open')}
          </Link>
        )}
      </div>
    </div>
  );
}
